import { addQueryParams } from '@modules/url-domain/main'
import type { Array } from 'effect'
import type { Company, CompanyDraft, Invoice, Vendor } from '@prisma/client'
import { pick } from 'remeda'
import { routes as codatRoutes } from 'server/modules/codat-adapter/module-routes'
import type { DisbursementDocumentArgs } from 'server/modules/disbursement-manager/entities'
import { DocumentLink } from 'server/modules/document-repository/entities'
import { nextApiRoutes as esignaturesAdapterApiRoutes } from 'server/modules/esignatures-adapter/routes'
import { HealthChatType } from 'server/modules/health-chat-service'
import { Invite } from 'server/modules/invite-manager'
import { LoanApplication } from 'server/modules/loan-application-manager/entities'
import type {
    LoanContext,
    LoanContextType,
} from 'server/modules/loan-manager/entities/loan-context'
import {
    contextTypeToUrlString,
    loanContextsToUrlPath,
} from 'server/modules/loan-manager/loan-context-urls'
import { nextApiRoutes as modulrAdapterApiRoutes } from 'server/modules/modulr-adapter/routes'
import type { OAuthInteractionId } from 'server/modules/oauth-authorization-server/entities'
import { AddInvoiceStepId } from 'containers/add-invoice/data/addInvoiceSteps'
import { AddInvoiceParams } from 'containers/add-invoice/steps/add-invoice-details/useQueryParams'
import { LoanQueryParams } from 'containers/admin/loans/types'
import { QueryParams as ComplianceQueryParams } from 'containers/compliance/checklist/useQueryParams'
import { DashboardAction } from 'containers/dashboard/hooks/useQueryParams'
import type { EsgProfileQueryParams } from 'containers/esg-profile/useQueryParams'
import { QueryParams as HealthQueryParams } from 'containers/health/hooks/useQueryParams'
import type { AddPaymentsQueryParams } from 'containers/invoices/add-payments/useQueryParams'
import { OnboardingStepId } from 'containers/onboarding/data/steps'

export type AddInvoiceOrigin = 'dashboard' | 'invoices' | 'calculator-popup' | 'xero'
export type InvoiceId = Invoice['id']
export type CompanyId = Company['id']
export type CompanyDraftId = CompanyDraft['id']
export type VendorId = Vendor['id']
export type CompanyTab = 'details' | 'people' | 'integrations' | 'documents'

export const SHOW_CALCULATOR_QUERY_PARAM = 'showCalc'

// waiting types
type LendingContractWaitResourceDescriptor = {
    waitFor: 'lending-contract'
    loanApplicationId: LoanApplication['id']
}
type GocardlessMandateWaitResourceDescriptor = {
    waitFor: 'gocardless-mandate'
    mandateId?: string
}
type WaitResourceDescriptor =
    | LendingContractWaitResourceDescriptor
    | GocardlessMandateWaitResourceDescriptor

export type InvoiceAddFileParams = {
    connectionFailed?: boolean
}

export const routes = {
    onboarding: {
        step: (stepId?: OnboardingStepId) =>
            stepId ? `/onboarding/${stepId}` : '/onboarding',
        initial: () => '/onboarding',
        prepareDashboard: () => {
            return '/onboarding/prepare-dashboard'
        },
    },
    invoices: {
        all: ({
            invoiceId,
            loanApplicationId,
        }: { invoiceId?: InvoiceId; loanApplicationId?: LoanApplication['id'] } = {}) => {
            return addQueryParams('/invoices', { invoiceId, loanApplicationId })
        },
        add: {
            step: ({
                step,
                params,
            }: {
                step: AddInvoiceStepId
                params?: AddInvoiceParams & InvoiceAddFileParams
            }) => addQueryParams(`/invoices/add/${step}`, params),
        },
        addPayments: ({
            invoiceId,
            payments,
            externalSettlements,
        }: { invoiceId: InvoiceId } & AddPaymentsQueryParams) =>
            addQueryParams(`/invoices/${invoiceId}`, {
                payments: payments?.length ? JSON.stringify(payments) : undefined,
                externalSettlements: externalSettlements?.length
                    ? JSON.stringify(externalSettlements)
                    : undefined,
            }),
        lendingContractUrl: ({ loanApplicationId }: { loanApplicationId: string }) =>
            `/api/lending-contract/${loanApplicationId}`,
        csv: ({ companyId }: { companyId: CompanyId }) =>
            addQueryParams(`/api/invoices/csv`, { companyId }),
    },
    drawdowns: {
        add: () => '/drawdowns/add',
        all: (args?: { loanApplicationId?: string }) =>
            addQueryParams(`/drawdowns`, args),
    },
    dashboard: (
        params?:
            | {
                  action: DashboardAction['requestCredit']
              }
            | {
                  action: DashboardAction['showLoanComplete']
                  invoiceCount?: number
              }
            | {
                  [SHOW_CALCULATOR_QUERY_PARAM]: true
              },
    ) => {
        return addQueryParams('/dashboard', params)
    },
    api: {
        uploadInvoice: () => '/api/invoices/upload',
        // TODO [231]: should we add another handler for failures to log them? not sure
        esignatures: esignaturesAdapterApiRoutes,
        postOpenBankingConnection: (args: { companyId: CompanyId }) =>
            addQueryParams(
                '/api/financial-data-provider/post-open-banking-connection',
                args,
            ),
        aiAdminAssistant: () => '/api/ai/admin-assistant',
        uploadDocument: (
            args: readonly Pick<
                DocumentLink,
                'associatedEntityId' | 'associatedEntityType'
            >[],
        ) => addQueryParams('/api/documents/upload', { links: JSON.stringify(args) }),
        document: (documentId: string) => `/api/documents/${documentId}`,
        invoiceDocumentThumbnail: (
            args: { invoiceDocumentId: string } | { invoiceId: string },
        ) => addQueryParams('/api/invoice-document-thumbnail', args),
        loanTapeDownload: (filename: string) =>
            addQueryParams('/api/admin/reports/loan-tape', { filename }),
        disbursementDocument: ({ loanApplicationId, type }: DisbursementDocumentArgs) =>
            `/api/admin/loans/${loanApplicationId}/documents/${type}`,
        modulr: modulrAdapterApiRoutes,
    },
    admin: {
        // the route to check you're logged in as an admin
        // could be killed if we have more suitable pages to check that
        home: () => '/admin',
        test: () => '/admin/test',
        logout: () => '/admin/logout',
        login: (queryParams?: { warning?: string }) =>
            addQueryParams('/admin/login', queryParams),
        invoices: {
            all: (queryParams?: { company?: Pick<Company, 'id' | 'name'> }) => {
                const entity = queryParams?.company
                    ? {
                          entityIdentifierType: 'company',
                          entityIdentifierId: queryParams?.company.id,
                          entityIdentifierLabel: queryParams?.company.name,
                      }
                    : {}
                return addQueryParams('/admin/invoices', {
                    ...entity,
                })
            },
            invoice: ({ invoiceId }: { invoiceId: string }) =>
                `/admin/invoices/${invoiceId}`,
        },
        loans: {
            all: (queryParams?: LoanQueryParams) => {
                const entity = (() => {
                    if (!queryParams) {
                        return {}
                    }
                    if ('company' in queryParams) {
                        return {
                            entityIdentifierType: 'company',
                            entityIdentifierId: queryParams.company.id,
                            entityIdentifierLabel: queryParams.company.name,
                        }
                    }
                    if ('invoice' in queryParams) {
                        return {
                            entityIdentifierType: 'invoice',
                            entityIdentifierId: queryParams.invoice.id,
                            entityIdentifierLabel:
                                queryParams.invoice.invoiceNumber ??
                                queryParams.invoice.id,
                        }
                    }
                    if ('vendor' in queryParams) {
                        return {
                            entityIdentifierType: 'vendor',
                            entityIdentifierId: queryParams.vendor.id,
                            entityIdentifierLabel: queryParams.vendor.name,
                        }
                    }
                    return {}
                })()
                return addQueryParams('/admin/loans', {
                    ...entity,
                    ...(queryParams && pick(queryParams!, ['statuses'])),
                })
            },
            loanApplication: ({
                loanApplicationId,
            }: {
                loanApplicationId: LoanApplication['id']
            }) => `/admin/loans/${loanApplicationId}`,
        },
        users: {
            all: (queryParams?: { companyId?: CompanyId }) =>
                addQueryParams('/admin/users', queryParams),
            user: ({ userId }: { userId: string }) => `/admin/users/${userId}`,
        },
        companies: {
            all: () => '/admin/companies',
            company: ({
                companyId,
                openHealthChat,
                type,
                chatId,
                extractedFromMessageIndex,
                questionId,
            }: {
                companyId: CompanyId
                openHealthChat?: true
                type?: HealthChatType
                chatId?: string
                extractedFromMessageIndex?: number
                questionId?: string
            }) =>
                addQueryParams(`/admin/companies/${companyId}`, {
                    openHealthChat,
                    type,
                    chatId,
                    extractedFromMessageIndex,
                    questionId,
                }),
            companyDraft: ({ companyDraftId }: { companyDraftId: CompanyDraftId }) => {
                return `/admin/companies/onboarding/${companyDraftId}`
            },
            financialReports: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}/financial-reports`
            },
            franklinReports: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}/franklin-reports`
            },
            creditReports: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}/credit-reports`
            },
            financialHealth: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}/financial-health`
            },
            escalations: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}/escalations`
            },
        },
        healthChat: ({ chatId }: { chatId: string }) => `/admin/health-chat/${chatId}`,
        vendors: {
            all: () => '/admin/vendors',
            vendor: ({ vendorId }: { vendorId: VendorId }) => {
                return `/admin/vendors/${vendorId}`
            },
        },
        credit: ({ id }: { id: CompanyId }) => `/admin/credit/${id}`,
        model: () => '/admin/model',
        discounts: () => '/admin/discounts',
        debentures: () => '/admin/debentures',
        availableFunds: () => '/admin/available-funds',
        rates: () => '/admin/rates',
        codat: codatRoutes,
        reports: (report?: string) =>
            report ? `/admin/reports/${report}` : '/admin/reports',
        investors: {
            all: () => '/admin/investors',
            investor: ({
                investorId,
                cycleNumber,
            }: {
                investorId: string
                cycleNumber?: number
            }) => addQueryParams(`/admin/investors/${investorId}`, { cycleNumber }),
            investorCycleReportCSV: (args: { investorId: string; cycleNumber: number }) =>
                addQueryParams(`/api/investor-portal/cycle-report/csv`, args),
            investorAccountsCSV: () => `/api/investor-portal/investor-accounts/csv`,
        },
        activities: () => '/admin/activities',
        portfolio: () => '/admin/portfolio',
        tasks: () => '/admin/tasks',
        gocardlessRequisitions: () => '/admin/gocardless-requisitions',
        hubspotCompanySearch: () => '/admin/hubspot-company-search',
        copperCompanySearch: () => '/admin/copper-company-search',
        creditAccount: {
            companiesCurrentCreditUtilisationCSV: () =>
                '/api/admin/credit-account/companies-current-credit-utilisation/csv',
            companiesTotalCreditUtilisationCSV: () =>
                '/api/admin/credit-account/companies-total-credit-utilisation/csv',
        },
        financialHistory: {
            transactionsCSV: ({
                companyId,
                externalAccountId,
            }: {
                companyId: CompanyId
                externalAccountId?: string
            }) =>
                addQueryParams(`/api/financial-history/transactions/csv`, {
                    companyId,
                    externalAccountId,
                }),
        },
        kyc: ({ userId }: { userId: string }) => `/api/admin/kyc/${userId}`,
        documents: ({ companyId }: { companyId: CompanyId }) =>
            `/admin/documents/${companyId}`,
    },
    compliance: {
        assistanceRequest: ({
            loanContexts,
        }: {
            loanContexts: Array.NonEmptyReadonlyArray<LoanContext>
        }) => {
            return `/compliance/${loanContextsToUrlPath(loanContexts)}/assistance-request`
        },
        checklist: ({
            loanContexts,
            expectComplete,
        }: {
            loanContexts: Array.NonEmptyReadonlyArray<LoanContext>
            expectComplete?: ComplianceQueryParams['expectComplete']
        }) => {
            return addQueryParams(
                `/compliance/${loanContextsToUrlPath(loanContexts)}/checklist`,
                {
                    expectComplete,
                },
            )
        },
    },
    collections: {
        directDebitSetup: (args: { redirect: string }) =>
            addQueryParams('/collections/direct-debit/setup', args),
    },
    lendingContracts: (
        args:
            | { loanContexts: Array.NonEmptyReadonlyArray<LoanContext> }
            | { loanContextType: LoanContextType },
    ) => {
        return 'loanContexts' in args
            ? `/lending-contracts/${loanContextsToUrlPath(args.loanContexts)}`
            : `/lending-contracts/${contextTypeToUrlString({
                  type: args.loanContextType,
              })}`
    },
    confirmingSignature: () => '/confirming-signature',
    home: (args?: { invite: Pick<Invite, 'invitedUserType' | 'id'> }) => {
        return addQueryParams(
            '/',
            args?.invite && {
                inviteId: args.invite.id,
                type: args.invite.invitedUserType,
            },
        )
    },
    privacyPolicy: () => 'https://www.kikin.io/privacy-policy',
    termsOfService: () => 'https://kikin.io/terms-of-service',
    // TODO [ISSUE 2025]: should be new terms of service for vendors
    termsOfServiceVendor: () => 'https://kikin.io/terms-of-service',
    kikin: () => 'https://www.kikin.io',
    esgProfile: (params?: EsgProfileQueryParams) =>
        addQueryParams('/esg-profile', params),
    health: (params?: HealthQueryParams) => addQueryParams('/health', params),
    company: (args?: { tab?: CompanyTab }) =>
        args?.tab ? `/company#${args.tab}` : '/company',
    additionalSecurity: () => '/company/additional-security',
    sendFeedback: () => '/send-feedback',
    support: () => '/support',
    checkEmail: (email: string) => addQueryParams('/check-email', { email }),
    pipedream: {
        submitFeedback: () => 'https://18feb9ecf2dd4770d1a2f2287a93b0b7.m.pipedream.net',
        redirectConfig: () => 'https://eovafvnfn7i2ibu.m.pipedream.net',
    },
    ecotreeCarbonOffsetCalculation: () =>
        'https://ecotree.green/en/how-much-co2-does-a-tree-absorb',
    govUK: () => 'http://www.gov.uk',
    calculator: () => '/calculator',
    impact: () => '/impact',
    vendor: {
        details: (args: { vendorId: string; bankAccountId: string }) =>
            addQueryParams(`/vendor/${args.vendorId}`, {
                bankAccountId: args.bankAccountId,
            }),
        bankAccountDetails: (args: { vendorId: string; bankAccountId: string }) =>
            `/vendor/${args.vendorId}/bank-account/${args.bankAccountId}`,
        login: (args: { vendorVerificationRequestId: string }) =>
            `/vendor/verification-request/${args.vendorVerificationRequestId}`,
        verificationSuccessful: (args: { vendorId: string }) =>
            `/vendor/${args.vendorId}/verification-successful`,
        home: () => '/vendor/home',
    },
    oauth: {
        interaction: (args: { id: OAuthInteractionId }) =>
            addQueryParams('/api/oauth/interaction', { uid: args.id }),
    },
    auth: {
        signin: (args: { oauthInteractionId: OAuthInteractionId }) =>
            addQueryParams('/', args),
        callbacks: {
            vendor: (args: { vendorVerificationRequestId: string }) =>
                addQueryParams('/api/auth/success/vendor', {
                    id: args.vendorVerificationRequestId,
                }),
            all: (args?: {
                inviteId?: string
                oauthInteractionId?: OAuthInteractionId
            }) => addQueryParams('/api/auth/success', args),
        },
    },
    investor: {
        home: () => '/investor/home',
    },
    debtManager: {
        cashflowCSV: ({ companyId }: { companyId?: CompanyId }) =>
            addQueryParams(`/api/debt-manager/cashflow/csv`, { companyId }),
        futureInstalmentsCSV: ({ company }: { company?: Pick<Company, 'id'> }) =>
            addQueryParams(`/api/debt-manager/future-instalments/csv`, {
                companyId: company?.id,
            }),
    },
    wait: (args: { redirect: string } & WaitResourceDescriptor) =>
        addQueryParams('/wait', args),

    // TODO [3169]: remove this
    test: {
        openBankingConnection: () => '/test/open-banking',
    },
    xero: {
        termsOfUse: () => 'https://www.xero.com/terms-of-use/',
    },
    documents: () => '/documents',
    account: () => '/account',
}
